import { ArrowDown, ArrowUp, CheckCircle2, Lightbulb, Plus, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { MIXTURE_COMPONENTS, componentName, type VesselItem } from './separationPlanner';

export interface PlanStepOption<Step extends string> {
  id: Step;
  label: string;
  property: string;
}

/** Ordered plan editor: add steps from a palette, reorder with buttons, remove. Stays editable after a run. Keyboard friendly. */
export function PlanBuilder<Step extends string>({
  steps,
  plan,
  maxSteps,
  onChange,
  noun,
}: {
  steps: readonly PlanStepOption<Step>[];
  plan: Step[];
  maxSteps: number;
  onChange: (plan: Step[]) => void;
  noun: string;
}) {
  const labelOf = (id: Step) => steps.find((step) => step.id === id)?.label ?? id;
  const move = (index: number, delta: number) => {
    const next = [...plan];
    const [item] = next.splice(index, 1);
    next.splice(index + delta, 0, item);
    onChange(next);
  };
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <div className="min-w-0">
        <p className="text-sm font-extrabold text-ink">Available {noun}s</p>
        <ul className="mt-2 grid gap-2" aria-label={`Available ${noun}s`}>
          {steps.map((step) => {
            const used = plan.includes(step.id);
            return (
              <li key={step.id}>
                <button
                  type="button"
                  disabled={used || plan.length >= maxSteps}
                  onClick={() => onChange([...plan, step.id])}
                  aria-label={`Add ${step.label}`}
                  className="flex w-full items-start gap-3 rounded-2xl border border-hairline bg-surface px-3 py-2 text-left transition-colors hover:border-brand-sky/50 disabled:cursor-not-allowed disabled:opacity-45"
                >
                  <Plus className="mt-0.5 h-4 w-4 shrink-0 text-brand-sky" aria-hidden="true" />
                  <span className="min-w-0">
                    <span className="block text-sm font-extrabold text-ink">
                      {step.label}
                      {used ? <span className="ml-2 text-xs font-bold text-slate2">(in plan)</span> : null}
                    </span>
                    <span className="block text-xs font-medium leading-5 text-slate2">{step.property}</span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>
      <div className="min-w-0">
        <p className="text-sm font-extrabold text-ink">
          Your order ({plan.length}/{maxSteps})
        </p>
        {plan.length === 0 ? (
          <p className="mt-2 rounded-2xl border border-dashed border-hairline bg-surface px-4 py-6 text-center text-sm font-bold text-slate2">
            Add {noun}s in the order you would do them.
          </p>
        ) : (
          <ol className="mt-2 grid gap-2" aria-label={`Your ${noun} order`}>
            {plan.map((id, index) => (
              <li
                key={id}
                className="flex items-center gap-2 rounded-2xl border border-brand-sky/30 bg-wash-sky px-3 py-2"
              >
                <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-ink text-xs font-extrabold text-white">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1 text-sm font-extrabold text-ink">{labelOf(id)}</span>
                <button
                  type="button"
                  disabled={index === 0}
                  onClick={() => move(index, -1)}
                  aria-label={`Move ${labelOf(id)} earlier`}
                  className="rounded-full border border-hairline bg-white p-1.5 text-ink disabled:opacity-35"
                >
                  <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  disabled={index === plan.length - 1}
                  onClick={() => move(index, 1)}
                  aria-label={`Move ${labelOf(id)} later`}
                  className="rounded-full border border-hairline bg-white p-1.5 text-ink disabled:opacity-35"
                >
                  <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
                <button
                  type="button"
                  onClick={() => onChange(plan.filter((step) => step !== id))}
                  aria-label={`Remove ${labelOf(id)}`}
                  className="rounded-full border border-hairline bg-white p-1.5 text-ink disabled:opacity-35"
                >
                  <X className="h-3.5 w-3.5" aria-hidden="true" />
                </button>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}

/** Shown on a result after the learner changed the plan, so an old result is never read as the new one. */
export function StaleResultNotice({ onRun, runLabel }: { onRun: () => void; runLabel: string }) {
  return (
    <div role="status" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-brand-sunshine/50 bg-wash-sunshine px-4 py-3">
      <p className="text-sm font-extrabold text-ink">You changed the plan. This result is for your previous plan.</p>
      <button type="button" onClick={onRun} className="rounded-full bg-ink px-4 py-2 text-sm font-extrabold text-white">
        {runLabel}
      </button>
    </div>
  );
}

export function ResultBanner({ success, title, children }: { success: boolean; title: string; children: ReactNode }) {
  return (
    <div
      role="status"
      className={`rounded-[1.5rem] border p-5 ${success ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'}`}
    >
      <div className="flex items-start gap-3">
        {success ? (
          <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-mint" aria-hidden="true" />
        ) : (
          <Lightbulb className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
        )}
        <div className="min-w-0">
          <p className="font-extrabold text-ink">{title}</p>
          <div className="mt-2 space-y-2 font-medium leading-7 text-slate2">{children}</div>
        </div>
      </div>
    </div>
  );
}

const COMPONENT_FILL: Record<string, string> = {
  salt: 'fill-white stroke-ink/40',
  sand: 'fill-brand-sunshine stroke-ink/30',
  iron: 'fill-ink stroke-ink',
  gravel: 'fill-slate2 stroke-ink/40',
  clay: 'fill-brand-coral stroke-ink/30',
  chalk: 'fill-stone2 stroke-ink/30',
  sulfur: 'fill-brand-sunshine stroke-ink/60',
  'copper-sulfate': 'fill-brand-sky stroke-ink/40',
  'dye-blue': 'fill-brand-sky stroke-ink/30',
  'dye-yellow': 'fill-brand-sunshine stroke-ink/30',
  'dye-red': 'fill-brand-coral stroke-ink/30',
};

const FORM_LABELS: Record<VesselItem['form'], string> = {
  solid: 'dry solid',
  suspended: 'undissolved grains in the liquid',
  dissolved: 'dissolved',
  liquid: 'liquid',
};

const BEAKER = { left: 30, right: 190, top: 22, bottom: 150 };
const LIQUID_TOP = 62;
const OIL_BOTTOM = 84;
const PARTICLES_PER_COMPONENT = 9;
const LARGE_PARTICLES = 5;

/** Deterministic spread in [0, 1) so particle positions are stable between renders. */
function spread(seed: number): number {
  const value = Math.sin(seed * 12.9898) * 43758.5453;
  return value - Math.floor(value);
}

function radiusFor(item: VesselItem): number {
  const size = MIXTURE_COMPONENTS[item.componentId]?.size;
  if (item.form === 'dissolved') return 1.8;
  if (size === 'large') return 7;
  if (size === 'fine') return 2;
  return 3.4;
}

function particlesFor(item: VesselItem, componentIndex: number, hasLiquid: boolean, liquidTop: number) {
  const size = MIXTURE_COMPONENTS[item.componentId]?.size;
  const count = size === 'large' ? LARGE_PARTICLES : PARTICLES_PER_COMPONENT;
  const r = radiusFor(item);
  const width = BEAKER.right - BEAKER.left - 2 * r - 8;
  return Array.from({ length: count }, (_, index) => {
    const seed = componentIndex * 31 + index * 7 + 1;
    const x = BEAKER.left + 4 + r + spread(seed) * width;
    let y: number;
    if (item.form === 'dissolved' || (item.form === 'suspended' && size === 'fine')) {
      y = liquidTop + 6 + spread(seed + 3) * (BEAKER.bottom - liquidTop - 12);
    } else {
      const pile = hasLiquid ? 22 : 34;
      y = BEAKER.bottom - r - 2 - spread(seed + 5) * pile;
    }
    return { x, y, r, key: `${item.componentId}-${index}` };
  });
}

/** Beaker schematic generated from the container state. Not to scale. */
export function VesselView({ items, caption }: { items: VesselItem[]; caption: string }) {
  const liquids = items.filter((item) => item.form === 'liquid');
  const hasOil = liquids.some((item) => MIXTURE_COMPONENTS[item.componentId]?.immiscibleWithWater);
  const hasLiquid = liquids.length > 0;
  const aqueousTop = hasOil ? OIL_BOTTOM : LIQUID_TOP;
  const particleItems = items.filter((item) => item.form !== 'liquid');
  const summary = items.length === 0
    ? 'empty'
    : items.map((item) => `${componentName(item.componentId)} (${FORM_LABELS[item.form]})`).join(', ');
  return (
    <figure className="min-w-0">
      <svg viewBox="0 0 220 165" role="img" aria-label={`${caption}: ${summary}`} className="mx-auto h-auto w-full max-w-xs">
        {hasLiquid && liquids.some((item) => !MIXTURE_COMPONENTS[item.componentId]?.immiscibleWithWater) ? (
          <rect x={BEAKER.left + 2} y={aqueousTop} width={BEAKER.right - BEAKER.left - 4} height={BEAKER.bottom - aqueousTop - 2} className="fill-wash-sky" />
        ) : null}
        {hasOil ? (
          <rect x={BEAKER.left + 2} y={LIQUID_TOP} width={BEAKER.right - BEAKER.left - 4} height={OIL_BOTTOM - LIQUID_TOP} className="fill-wash-sunshine stroke-brand-sunshine" strokeDasharray="4 3" />
        ) : null}
        {particleItems.flatMap((item, index) =>
          particlesFor(item, index, hasLiquid, aqueousTop).map((particle) => (
            <circle
              key={particle.key}
              cx={particle.x}
              cy={particle.y}
              r={particle.r}
              className={COMPONENT_FILL[item.componentId] ?? 'fill-slate2 stroke-ink/30'}
              strokeWidth={0.8}
            />
          )),
        )}
        <path
          d={`M${BEAKER.left} ${BEAKER.top} L${BEAKER.left} ${BEAKER.bottom} Q${BEAKER.left} ${BEAKER.bottom + 6} ${BEAKER.left + 8} ${BEAKER.bottom + 6} L${BEAKER.right - 8} ${BEAKER.bottom + 6} Q${BEAKER.right} ${BEAKER.bottom + 6} ${BEAKER.right} ${BEAKER.bottom} L${BEAKER.right} ${BEAKER.top}`}
          className="fill-none stroke-ink"
          strokeWidth={2}
        />
        {hasOil ? <text x={BEAKER.right - 6} y={LIQUID_TOP + 14} textAnchor="end" className="fill-ink text-[9px] font-bold">oil layer</text> : null}
      </svg>
      <figcaption className="mt-2 text-center text-xs font-extrabold text-ink">{caption}</figcaption>
      <ul className="mt-2 flex flex-wrap justify-center gap-1.5" aria-label={`${caption} contents`}>
        {items.length === 0 ? (
          <li className="text-xs font-bold text-slate2">Empty</li>
        ) : (
          items.map((item) => (
            <li key={item.componentId} className="rounded-full border border-hairline bg-white px-2.5 py-1 text-xs font-bold text-ink-soft">
              {componentName(item.componentId)}
              {MIXTURE_COMPONENTS[item.componentId]?.formula ? ` (${MIXTURE_COMPONENTS[item.componentId]?.formula})` : ''}
              <span className="text-slate2"> · {FORM_LABELS[item.form]}</span>
            </li>
          ))
        )}
      </ul>
    </figure>
  );
}
